import { useTwoUIFlights } from "@amx/ux-twoui-telemetry";
import { Fluent2WebLightTheme } from "@fluentui/fluent2-theme";
import { getRTL, ThemeProvider, useTheme as fluentUseTheme } from "@fluentui/react";
import { FluentProvider, IdPrefixProvider } from "@fluentui/react-components";
import React from "react";
import { themeMap } from "../themeList";
import { getFluentV9ThemeFromV8Theme } from "../themes";
import { amcDefaultFontStyle, amcFonts } from "../themes/AmcTheme";
import { getAmcFluentV9Theme } from "../themes/v9/AmcFluentV9Theme";
import { Theme, ThemeVariant } from "../types";

export interface ThemedContainerProps {
    readonly variant: ThemeVariant;
    /**
     * Do not render a div as the root element for the ThemedContainer component.
     */
    readonly noRootElement?: boolean;
    /*
     * This id can be used to link TwoUI Dialog components to the theme provider.
     * This allows focus to target the first interactive element when using keyboard navigation, without it your experience could experience accessibility bugs
     * FluentUI is currently investigating long term fix for this which is being tracked in Bug 15780 (https://dev.azure.com/microsoftdesign/fluent-ui/_workitems/edit/15780)
     */
    readonly id?: string;
    /**
     * Allows to define a prefix that will be used for all IDs generated by useId() hook.
     * It's useful to avoid collisions between different bundles.
     */
    readonly idPrefix?: string;
}

const twoUIV9TextFlightName = "TwoUIV9Text";

/**
 * https://microsoft.visualstudio.com/Universal%20Store/_wiki/wikis/Universal%20Store_wiki/63637/Theming
 */
export const ThemedContainer: React.FC<ThemedContainerProps> = ({
    variant,
    noRootElement,
    id,
    idPrefix,
    children
}) => {
    // undefined rootElement will make ThemeProvider use the default (div)
    const rootElement = noRootElement ? Fragment : undefined;

    // update default font family
    const currTheme = { ...themeMap[variant], defaultFontStyle: amcDefaultFontStyle, fonts: amcFonts };

    // Get flights
    const twoUIFlights = useTwoUIFlights();

    const mainContent = (
        /*
         * This id can be used to link TwoUI Dialog components to the theme provider.
         * This allows focus to target the first interactive element when using keyboard navigation, without it your experience could experience accessibility bugs
         * FluentUI is currently investigating long term fix for this which is being tracked in Bug 15780 (https://dev.azure.com/microsoftdesign/fluent-ui/_workitems/edit/15780)
         */
        <ThemeProvider id={id} theme={currTheme} as={rootElement}>
            {children}
        </ThemeProvider>
    );

    // The AMC variant uses the FluentUI V9 theme directly instead of mapping,
    // because the AMC variant brand colors align with the FluentUI ones.
    // Other variants use the V8-to-V9 mapping to maintain their brand color.
    const fluentV9Theme = React.useMemo(() => {
        if (twoUIFlights?.includes(twoUIV9TextFlightName)) {
            return variant === "AMC" ? getAmcFluentV9Theme() : getFluentV9ThemeFromV8Theme(currTheme);
        }
        return undefined;
    }, [variant, currTheme]);

    if (twoUIFlights?.includes(twoUIV9TextFlightName)) {
        const isRTL = getRTL();
        // Wrapping the V9 Shim Theme, then the V9 theme (for v9 components), then V8 theme.
        return (
            <ThemeProvider theme={Fluent2WebLightTheme} as={rootElement}>
                <IdPrefixProvider value={idPrefix}>
                    <FluentProvider theme={fluentV9Theme} dir={isRTL ? "rtl" : "ltr"}>
                        {mainContent}
                    </FluentProvider>
                </IdPrefixProvider>
            </ThemeProvider>
        );
    } else if (twoUIFlights?.includes("Fluentv9Theming")) {
        // Wrapping the V9 Shim Theme, then V8 theme.
        return (
            <ThemeProvider theme={Fluent2WebLightTheme} as={rootElement}>
                {mainContent}
            </ThemeProvider>
        );
    }
    return mainContent;
};

/**
 * Component to mimic React.Fragment and bypass the element that ThemeProvider renders
 * as root before its children.
 * Using React.Frament shows the warning: 'Invalid prop `theme` supplied to `React.Fragment`'.
 */
// tslint:disable-next-line:no-class we need a class component because it needs to support the ref attribute.
class Fragment extends React.Component {
    public render() {
        return this.props.children;
    }
}

/**
 * Casts Fluent's `useTheme` to return the type of our ExtendedTheme. The ExtendedTheme includes
 * any custom keys (as defined in TwoUISemanticColors). If a component is trying to access one of these
 * colours, but isn't within the context of one of our themes, the key won't exist in the semanticColors
 * object of our Theme, and will return undefined. This could break components, but it's an inescapable
 * part of defining custom keys - that if the component isn't wrapped in a custom theme, no default will exist
 */
export const useTheme = () => fluentUseTheme() as Theme;
